import { useEffect, useMemo, useState } from 'react';
import {
  Cell, DrillBoardRecord, DrillMode, DrillModeId, DrillSessionSummary, MODES, flushPending,
  formatMs, isSprint, pendingCount, regionsFromCode,
} from '../drill';
import { api } from '../api';
import { getPlayerToken } from '../cookie';
import { StaticBoard } from './StaticBoard';
import { SessionChart } from './SessionChart';

interface Props {
  mode: DrillMode;
  onChangeMode: (id: DrillModeId) => void;
  onBack: () => void;
}

const OUTCOME_LABEL: Record<DrillBoardRecord['outcome'], string> = {
  found: 'Found',
  missed: 'Missed',
  skipped: 'Skipped',
};

/** Rows and columns are counted from 1 here — this is read by a person, not indexed by code. */
function square(cell: Cell): string {
  return `r${cell.row + 1}c${cell.col + 1}`;
}

function modeOf(id: string) {
  return MODES.find(m => m.id === id);
}

function when(at: number): string {
  const d = new Date(at);
  const sameDay = d.toDateString() === new Date().toDateString();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return sameDay ? `Today ${time}` : `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`;
}

/**
 * One board of a session: what it looked like, and what the player did to it.
 *
 * The solution is drawn as queens with the ones actually placed ringed, and a wrong queen is
 * marked ✕ where it went — so a missed board shows both what was asked and what was answered
 * without needing a caption to reconcile them. The caption names the squares anyway, because
 * "where did I put it" is the question this page exists to answer and a 76px board is not a
 * comfortable place to read a coordinate off.
 */
function BoardRow({ board }: { board: DrillBoardRecord }) {
  const regions = useMemo(() => regionsFromCode(board.cells, board.size), [board.cells, board.size]);
  const queens = useMemo<Cell[]>(() => board.solution.map((col, row) => ({ row, col })), [board.solution]);
  const ringed = useMemo(() => new Set(board.placed.map(c => `${c.row},${c.col}`)), [board.placed]);

  return (
    <li className={`drill-hist-board is-${board.outcome}`}>
      <span className="drill-hist-thumb">
        <StaticBoard regions={regions} queens={queens} mark={board.miss} ringed={ringed} />
      </span>
      <span className="drill-hist-board-text">
        <span className="drill-hist-outcome">{OUTCOME_LABEL[board.outcome]}</span>
        <span className="drill-hist-detail">
          {board.placed.length > 0 && <>placed {board.placed.map(square).join(', ')}</>}
          {board.placed.length > 0 && board.miss && ' · '}
          {board.miss && <>wrong queen on {square(board.miss)}</>}
          {board.placed.length === 0 && !board.miss && <>no queen placed</>}
        </span>
        <span className="drill-hist-detail">{formatMs(board.ms)}</span>
      </span>
    </li>
  );
}

/** Boards are fetched when a session is opened, not with the list — most never get opened. */
function SessionCard({ session, open, onToggle }: {
  session: DrillSessionSummary; open: boolean; onToggle: () => void;
}) {
  const [boards, setBoards] = useState<DrillBoardRecord[] | null>(null);
  const [error, setError] = useState(false);
  const mode = modeOf(session.mode);
  const sprint = mode ? isSprint(mode) : false;

  useEffect(() => {
    if (!open || boards) return;
    let live = true;
    api.getDrillBoards(session.id, getPlayerToken())
      .then(r => { if (live) setBoards(r.boards); })
      .catch(() => { if (live) setError(true); });
    return () => { live = false; };
  }, [open, boards, session.id]);

  return (
    <div className={`drill-hist-session${open ? ' is-open' : ''}`}>
      <button className="drill-hist-head" onClick={onToggle} aria-expanded={open}>
      {/* A run wears its own score: a session is remembered by its clock, a sprint by how many
          boards it got through. */}
      <span className="drill-hist-clock">
        {sprint ? `${session.found}` : formatMs(session.clockMs)}
      </span>
        <span className="drill-hist-meta">
          <span className="drill-hist-mode">{mode?.label ?? session.mode}</span>
          <span className="drill-hist-detail">
            {when(session.at)} · {sprint ? `${session.found} found in ${formatMs(session.totalMs)}` : `${session.found} found`}
            {session.missed > 0 && <> · {session.missed} missed <strong>{sprint ? '−' : '+'}{session.penaltyMs / 1000}s</strong></>}
            {session.skipped > 0 && <> · {session.skipped} skipped</>}
          </span>
        </span>
        <span className="drill-hist-chevron" aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        error ? <p className="drill-hist-detail drill-hist-pad">Could not load this session's boards.</p>
          : !boards ? <p className="drill-hist-detail drill-hist-pad">Loading…</p>
            : (
              <ol className="drill-hist-boards">
                {boards.map((board, i) => <BoardRow key={i} board={board} />)}
              </ol>
            )
      )}
    </div>
  );
}

/**
 * Every speed drill session this player has finished, read back from the server.
 *
 * The sessions are recorded for the modelling work — per-board times against the board that was
 * actually on screen — and this page is the human view of that record: which boards beat you, and
 * exactly where you put the queen. Deliberately apart from the player pages: a drill answer is not
 * a solve, and none of this belongs in a record of puzzles solved.
 */
export function DrillHistoryView({ mode, onChangeMode, onBack }: Props) {
  const [sessions, setSessions] = useState<DrillSessionSummary[] | null>(null);
  const [error, setError] = useState(false);
  // Everything starts collapsed: the list is the page, and one session opened for you is one you
  // did not choose to open.
  const [openId, setOpenId] = useState<number | null>(null);
  const [pending, setPending] = useState(0);

  useEffect(() => {
    let live = true;
    // Send anything still queued from a failed upload first, so what loads is up to date.
    flushPending(api.postDrillSession)
      .catch(() => pendingCount())
      .then(left => {
        if (live) setPending(left ?? 0);
        return api.getDrillSessions(getPlayerToken());
      })
      .then(r => { if (live) setSessions(r.sessions); })
      .catch(() => { if (live) setError(true); });
    return () => { live = false; };
  }, []);

  // One version at a time: a 4×4 clock, a 5×5 clock and a sprint's board count are three different
  // measurements, and a list that interleaves them cannot be read as a trend of anything.
  const mine = useMemo(
    () => (sessions ?? []).filter(s => s.mode === mode.id),
    [sessions, mode.id],
  );
  const sprint = isSprint(mode);

  const totals = useMemo(() => ({
    sessions: mine.length,
    boards: mine.reduce((n, s) => n + s.boardCount, 0),
    best: mine.reduce<number | null>((b, s) => (b === null || s.clockMs < b ? s.clockMs : b), null),
  }), [mine]);

  return (
    <div id="view-drill-history">
      <header className="game-header">
        <div className="game-header-inner">
          <button className="btn-back" onClick={onBack}>←<span className="hide-mobile"> Tek Mode</span></button>
          <h2>
            <span className="game-title-name is-worded">Tek Mode history</span>
            <span className="multi-tag" title="Recorded to study how long a board takes to read — kept out of your player stats">
              not in your stats
            </span>
          </h2>
          <div className="game-nav" />
        </div>
      </header>

      <main className="drill-hist-main">
        <div className="drill-modes" role="tablist" aria-label="Tek Mode version">
          {MODES.map(m => (
            <button
              key={m.id}
              role="tab"
              aria-selected={m.id === mode.id}
              className={`drill-mode${m.id === mode.id ? ' is-on' : ''}`}
              onClick={() => onChangeMode(m.id)}
            >
              {m.label}
            </button>
          ))}
        </div>

        {error && <p className="error">Could not load your sessions.</p>}
        {!error && !sessions && <p className="loading">Loading…</p>}
        {!error && sessions && mine.length === 0 && (
          <p className="drill-log-empty">
            No finished {mode.label} sessions yet. A session lands here once you finish the run — an
            abandoned one isn't recorded.
          </p>
        )}
        {mine.length > 0 && (
          <>
            <p className="drill-hist-summary">
              <strong>{totals.sessions}</strong> session{totals.sessions === 1 ? '' : 's'} ·{' '}
              <strong>{totals.boards}</strong> boards
              {!sprint && totals.best !== null && <> · fastest <strong>{formatMs(totals.best)}</strong></>}
              {pending > 0 && <> · <strong>{pending}</strong> waiting to upload</>}
            </p>
            {/* A trend needs a few runs behind it; two points is a line through noise. */}
            {!sprint && mine.length >= 3 && <SessionChart sessions={mine} />}
            {!sprint && mine.length < 3 && (
              <p className="drill-chart-note">
                {3 - mine.length} more session{3 - mine.length === 1 ? '' : 's'} and your times get plotted here.
              </p>
            )}
            <div className="drill-hist-list">
              {mine.map(session => (
                <SessionCard
                  key={session.id}
                  session={session}
                  open={openId === session.id}
                  onToggle={() => setOpenId(openId === session.id ? null : session.id)}
                />
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
